import React, { Dispatch, SetStateAction } from "react";
import { camelCase, get, set, first, keys } from "lodash";
import {
  BetaFeature as BetaFeatureId,
  BetaFeature,
  BetaFeatureStatus,
  DismissOrigin,
  Whitelist
} from "@tvg/sh-lib-beta-program/types";
import { State } from "@tvg/sh-lib-beta-program/types/redux";
import {
  closeFeaturesModal,
  openFeaturesModal
} from "@tvg/sh-lib-beta-program";
import * as Apptentive from "@tvg/utils/apptentive-events";
import { Dispatch as ReduxDispatch } from "@reduxjs/toolkit";
import {
  gtmClickCTAExitModal,
  gtmClickCTAOnboardingModal,
  gtmOpenFeedbackWidget,
  gtmSubmitFeedbackWidget,
  gtmViewOnboardingModal
} from "@tvg/sh-lib-beta-program/gtm";
import {
  areNewFeaturesAvailable,
  enrollInBeta,
  getBetaFeatureFromStorage,
  hasBetaStatusInStorage,
  isAnyFeatureInCooldown,
  isEligibleForAny,
  isOptedIn,
  saveDismissStatus,
  syncStorageAndCookie,
  unenrollInBeta
} from "@tvg/sh-lib-beta-program/utils/handlers";
import { Theme } from "@tvg/design-system/src/theming/types";
import { StoryblokStory } from "@tvg/sh-lib-storyblok/src/types";
import { fetchStoryblok } from "@tvg/sh-lib-storyblok/src/fetchStoryblok";
import { STORYBLOK_CONTENT_PATH } from "@tvg/sh-lib-storyblok/src/constraints/config";
import tvgConf from "@tvg/conf";

import { QA_LABELS } from "../../utils/constants";
import { FeaturePicker } from "./styled-components";
import {
  getBetaProgramContent,
  getBetaMyStableContent
} from "../../redux/selectors";
import { FeaturesModalConfig } from "../../types";
import {
  BetaFeatures,
  ListItem,
  StoryblokBetaFeatureContent,
  StoryblokBetaFeatureContentFeature,
  BetaFeature as StoryblokBetaFeature
} from "./types";

export const handleClose = (dispatch: ReduxDispatch) =>
  dispatch(closeFeaturesModal());

export const renderListItems = (
  items: Array<ListItem>,
  selectedFeature: BetaFeature,
  setSelectedFeature: Dispatch<SetStateAction<BetaFeature>>,
  theme: Theme
): JSX.Element[] =>
  items.map((item, index) => (
    <FeaturePicker
      key={camelCase(item.value) + "-" + index.toString()}
      onClick={() => setSelectedFeature(item.value)}
      data-qa-label={QA_LABELS.FEATURE_LIST_ITEM}
      isActive={item.value === selectedFeature}
      theme={theme}
    >
      {item.label}
    </FeaturePicker>
  ));

export const getFeatureContent = (feature: BetaFeature) => (state: State) => {
  switch (feature) {
    case BetaFeature.PROGRAM_PAGE: {
      return getBetaProgramContent(state);
    }
    case BetaFeature.MY_STABLE: {
      return getBetaMyStableContent(state);
    }
    default:
      return getBetaProgramContent(state);
  }
};

export const onConfirmExit =
  (
    accountNumber: string,
    whitelist: Whitelist[],
    betaFeature: BetaFeature,
    setIsExiting: Dispatch<SetStateAction<boolean>>
  ) =>
  (confirmTxt: string | void) => {
    gtmClickCTAExitModal(confirmTxt || "");
    unenrollInBeta(accountNumber, whitelist, betaFeature);
    setIsExiting(false);
  };

export const onCancelExit =
  (
    accountNumber: string,
    whitelist: Whitelist[],
    betaFeature: BetaFeature,
    setIsExiting: Dispatch<SetStateAction<boolean>>
  ) =>
  (confirmTxt: string | void) => {
    gtmClickCTAExitModal(confirmTxt || "");
    setIsExiting(false);
    closeSurveyAfterSubmit(accountNumber, whitelist, betaFeature);
  };

export const openApptentiveFeedback = () => {
  gtmOpenFeedbackWidget();
  Apptentive.events.showExitNewFeedbackBetaProgramSurvey();
};

export const submitApptentiveFeedback = () => {
  const submit = () => {
    gtmSubmitFeedbackWidget();
    document.removeEventListener("apptentive:survey:submit", submit);
  };

  document.addEventListener("apptentive:survey:submit", submit);
};

export const closeApptentiveFeedback = (
  setIsExiting: Dispatch<SetStateAction<boolean>>
) => {
  const closeButtonX = document.querySelector(
    ".apptentive-appbar__actions :last-child"
  );

  const closeSurvey = () => {
    setIsExiting(true);
    closeButtonX?.removeEventListener("click", closeSurvey);
  };

  closeButtonX?.addEventListener("click", closeSurvey);
};

export const closeSurveyAfterSubmit = (
  accountNumber: string,
  whiteList: Whitelist[],
  betaFeature: BetaFeature
) => {
  const closeButton = document.getElementsByClassName(
    "apptentive-button--primary"
  );

  const closeSurvey = () => {
    unenrollInBeta(accountNumber, whiteList, betaFeature);
    closeButton[0]?.removeEventListener("click", closeSurvey);
  };

  closeButton[0]?.addEventListener("click", closeSurvey);
};

export const unenrollUser = (
  accountNumber: string,
  whitelist: Whitelist[],
  betaFeature: BetaFeature,
  setIsExiting: Dispatch<SetStateAction<boolean>>
) => {
  openApptentiveFeedback();
  submitApptentiveFeedback();
  closeApptentiveFeedback(setIsExiting);

  closeSurveyAfterSubmit(accountNumber, whitelist, betaFeature);
};

export const getCTA =
  (
    isUserEnrolled: boolean,
    betaFeature: BetaFeature,
    accountNumber: string,
    whitelist: Whitelist[],
    setIsExiting: Dispatch<SetStateAction<boolean>>,
    redirectUrl: string | undefined,
    dispatch: ReduxDispatch,
    actionText: string
  ) =>
  () => {
    gtmClickCTAOnboardingModal(actionText);
    if (isUserEnrolled) {
      handleClose(dispatch);
      return unenrollUser(accountNumber, whitelist, betaFeature, setIsExiting);
    }
    return enrollInBeta(accountNumber, whitelist, betaFeature, redirectUrl);
  };

export const generateModalConfig = (
  isUserEnrolled: boolean
): FeaturesModalConfig =>
  isUserEnrolled
    ? {
        ctaText: "Unenroll",
        ctaVariant: "secondary",
        tagVariant: "optedin",
        tagText: "User is enrolled"
      }
    : {
        ctaText: "Enroll",
        ctaVariant: "primary",
        tagVariant: "info",
        tagText: "User is not enrolled"
      };

export const getEnrollmentDate = (
  accountNumber: string,
  betaFeature: BetaFeature
) => {
  const storage = getBetaFeatureFromStorage(betaFeature, accountNumber);
  const timestamp = storage?.match(/\d+/)?.pop() || "";
  const date = new Date(parseInt(timestamp, 10));
  return date.toDateString();
};

export const openModalOnMount =
  (
    accountNumber: string,
    whitelist: Whitelist[],
    hasBetaFeaturesLoaded: boolean,
    dispatch: ReduxDispatch
  ) =>
  () => {
    syncStorageAndCookie(accountNumber);
    if (
      hasBetaFeaturesLoaded &&
      isEligibleForAny(accountNumber, whitelist) &&
      !isOptedIn(accountNumber) &&
      (areNewFeaturesAvailable(accountNumber, whitelist) ||
        (!isAnyFeatureInCooldown(accountNumber, BetaFeatureStatus.UNENROLL) &&
          !isAnyFeatureInCooldown(accountNumber, BetaFeatureStatus.DISMISS)))
    ) {
      dispatch(openFeaturesModal());
    }
  };

export const closeModalAndDismissFeature =
  (
    dispatch: ReduxDispatch,
    accountNumber: string,
    whitelist: Whitelist[],
    origin: DismissOrigin,
    selectedFeature: BetaFeature
  ) =>
  () => {
    // Workaround to avoid the double execution of the fn below triggered by DS modal
    if (
      !hasBetaStatusInStorage(BetaFeatureStatus.DISMISS, accountNumber) ||
      !isAnyFeatureInCooldown(accountNumber, BetaFeatureStatus.DISMISS)
    )
      saveDismissStatus(accountNumber, selectedFeature, whitelist, origin);

    handleClose(dispatch);
  };

export const executeGtmOnOpen =
  (isOpen: boolean, selectedFeature: BetaFeature) => () => {
    if (isOpen) gtmViewOnboardingModal(selectedFeature);
  };

export const formatStoryblokContent = (
  storyblokResponse:
    | StoryblokBetaFeatureContentFeature
    | StoryblokStory<StoryblokBetaFeatureContentFeature>,
  isLink = false
): StoryblokBetaFeature | null => {
  const story = isLink
    ? (storyblokResponse as StoryblokBetaFeatureContentFeature)?.link?.story
    : storyblokResponse;
  const content = get(story, "content");

  if (!content) {
    return null;
  }

  return {
    name: story.name,
    enroll: {
      description: content.enroll_description,
      details: content.enroll_details
    },
    unroll: {
      description: content.unroll_description,
      details: content.unroll_details
    },
    welcome: {
      description: content.welcome_description,
      title: content.welcome_title,
      subtitle: content.welcome_subtitle
    },
    exit: {
      description: content.exit_description,
      title: content.exit_title
    }
  };
};

export const formatStoryblokContentList = (
  storyblokFeatures: StoryblokStory<StoryblokBetaFeatureContent>,
  features: Array<Whitelist>
): BetaFeatures =>
  get(storyblokFeatures, "content.features", {}).reduce(
    (
      accFeatures: BetaFeatures,
      rawFeature: StoryblokBetaFeatureContentFeature
    ) => {
      const story = rawFeature?.link?.story;
      const content = get(story, "content");
      const slug = get(story, "slug");

      if (
        content &&
        features.some((userFeatures) => userFeatures.id === slug)
      ) {
        set(
          accFeatures,
          story.slug as BetaFeature,
          formatStoryblokContent(rawFeature, true)
        );
      }

      return accFeatures;
    },
    {}
  );

export const fetchStoryblokFeatures = async (
  whitelistFeatures: Array<Whitelist>
) => {
  try {
    const storyblokFeatures = await fetchStoryblok<StoryblokBetaFeatureContent>(
      STORYBLOK_CONTENT_PATH.BETA_PROGRAM,
      "",
      tvgConf().config().storyblokVersion,
      undefined,
      {
        resolve_links: "story"
      }
    );

    return formatStoryblokContentList(storyblokFeatures, whitelistFeatures);
  } catch (e) {
    console.error(`Error trying fetch storyblok in beta features:`, e);
    return {} as BetaFeatures;
  }
};

export const fetchStoryblokFeature = async (feature: BetaFeatureId) => {
  try {
    const storyblokFeature =
      await fetchStoryblok<StoryblokBetaFeatureContentFeature>(
        STORYBLOK_CONTENT_PATH.BETA_PROGRAM,
        `features/${feature}`,
        tvgConf().config().storyblokVersion
      );

    return formatStoryblokContent(storyblokFeature, false);
  } catch (e) {
    console.error(`Error trying fetch storyblok in beta features:`, e);
    return null;
  }
};

export const getFirstBetaFeature = (betaFeatures: BetaFeatures) =>
  first(keys(betaFeatures)) as BetaFeature;

export const getBetaFeatureList = (betaFeatures: BetaFeatures) =>
  Object.entries(betaFeatures).map(([betaFeatureId, feature]) => ({
    label: feature.name,
    value: betaFeatureId as BetaFeatureId
  }));
